import {
    addToRegistry,
    hideProductRegistries,
    promptRegistryCreate,
    showProductRegistries
} from '../actions/registryActions';

import ENUMS from '../enums/registryEnums';

import { getTargetedProduct } from '../../../product/js/utils/utils';

/**
 * Get the add-to-registry success messaging
 * @param  {function} getState  Redux state function
 * @param  {object} registry    Registry to which an item was successfully added
 * @return {object}             Success messaging
 */
export function getAddedToRegistryMessaging(state, registry) {
    const product = getTargetedProduct(state);
    return {
        success: ENUMS.ADDED_TO_REGISTRY_SUCCESS_SLUG,
        successParams: [
            product.productMeta.displayName,
            registry.event.name
        ]
    };
}

/**
 * Get a registry by index from a user's registries list
 * @param  {function} getState Redux state function
 * @param  {number} index      Target registry index
 * @return {object}            Target registry
 */
export function getRegistryFromIndex(state, index) {
    return state.registry.registries[index];
}

/**
 * Get the target action for the product add-to-registry flow
 * @param  {function} getState  Redux state function
 * @param  {object} action      Redux action
 * @return {object}             Target action containing action type and payload
 */
export function getProductAddToRegistryAction(state, action) {
    const registry = state.registry;
    const meta = action.meta;
    let targetedProduct;

    if (action.meta.component.forFamilyProduct) {
        const familyProductIndex = meta.component.multipleIndex;
        targetedProduct = state.product.familyProducts[familyProductIndex];
    } else {
        targetedProduct = getTargetedProduct(state);
    }

    if (registry.registries.length > 1) {
        if (targetedProduct.productRegistry.showProductRegistries) {
            return { type: hideProductRegistries, payload: {}, meta };
        }

        return { type: showProductRegistries, payload: {} };
    } else if (registry.registries.length === 1) {
        return { type: addToRegistry, payload: { selectedRegistry: registry.registries[0] } };
    }

    return { type: promptRegistryCreate, payload: {} };
}

/**
 * Get the registry item's requested quantity for
 * the Registrant's quickshop
 * @param  {function} getState  Redux state function
 * @return {number}             Requested quantity
 */
export function getRegistryItemRequestedQuantity(state) {
    let quantity = state.registry.requestedQuantity;

    if (_.isUndefined(quantity) || _.isNull(quantity)) {
        quantity = getTargetedProduct(state).productRegistry.selectedItem.requested_quantity;
    }

    return quantity;
}

/**
 * Get the registry item's purchased quantity for
 * the Registrant's quickshop
 * @param  {function} getState  Redux state function
 * @return {number}             Purchased quantity
 */
export function getRegistryItemPurchasedQuantity(state) {
    let quantity = state.registry.purchasedQuantity;

    if (_.isUndefined(quantity) || _.isNull(quantity)) {
        quantity = getTargetedProduct(state).productRegistry.selectedItem.purchased_quantity;
    }

    return quantity;
}

/**
 * When in the Registry Quickshop as a Registrant,
 * has the selected item already been purchased?
 * @param  {function}  getState Redux state function
 * @return {Boolean}            True, if the selected item has been purchased; false, otherwise.
 * @param  {object} action      Redux action
 */
export function hasQuickshopItemBeenPurchased(state) {
    return state.quickshop.product.productRegistry.selectedItem.purchased_quantity > 0;
}

/**
 * When in the Registry Quickshop as a Registrant,
 * has the requested quantity amount changed?
 * @param  {function}  getState Redux state function
 * @return {Boolean}            True, if requested quantity has changed; false, otherwise.
 */
export function hasQuickshopRequestedQuantityUpdate(state) {
    return !_.isUndefined(state.registry.requestedQuantity);
}

/**
 * When in the Registry Quickshop as a Registrant,
 * has the remaining quantity amount changed?
 * @param  {function}  getState Redux state function
 * @return {Boolean}            True, if requested quantity has changed; false, otherwise.
 */
export function hasQuickshopRemainingQuantityUpdate(state) {
    return !_.isUndefined(state.registry.purchasedQuantity);
}

/**
 * When in the Registry Quickshop as a Registrant,
 * has the selected sku been changed?
 * @param  {function}  getState Redux state function
 * @return {Boolean}            True, if the selected sku has changed; false, otherwise.
 */
export function hasQuickshopSkuUpdate(state) {
    const product = state.quickshop.product;
    const registryPrimarySlice = product.productRegistry.selectedItem.catalogInfo.skuInfo.primarySlice;

    return product.selectedSku.sku.skuId !== registryPrimarySlice.sliceItems[0].includedSkus[0].skuId;
}

/**
 * Find a replace a user's registry with an updated version
 * of it from the server.
 * @param  {function} getState  Redux state function
 * @param  {object} registry    Updated registry
 * @return {array}              User's registry list
 */
export function replaceRegistry(state, registry) {
    const registries = _.cloneDeep(state.registry.registries);
    const index = _.findIndex(registries, (r) => r.id === registry.id);

    registries[index] = registry;

    return registries;
}
